import React from 'react'
import { Controller } from 'react-hook-form'
import cx from 'classnames'

import { getDefaultLabels } from './model'

import type { ControlledFieldProps, FormFieldBaseProps } from '../../types'
import styles from './Slider.module.scss'

type SliderProps = FormFieldBaseProps & {
  labels?: string[]
  max?: number
  min?: number
  step?: number
  value?: number
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  onChange?: React.ChangeEventHandler<HTMLInputElement>
}

type LabelProps = { ariaLabel?: string; children?: React.ReactNode }

const Label = ({ ariaLabel, children }: LabelProps) => (
  <span aria-label={ariaLabel} className={styles.label} role="presentation">
    {children}
  </span>
)

export const Slider = React.forwardRef<HTMLInputElement, SliderProps>(
  (
    {
      fieldClassName,
      id,
      max = 1,
      min = 0,
      labels = getDefaultLabels(),
      name,
      step = 0.01,
      value,
      onBlur,
      onChange,
      isDisabled = false,
    },
    ref,
  ) => {
    const [minLabel, maxLabel] = labels

    return (
      <div className={styles.container}>
        <Label ariaLabel={minLabel}>{minLabel}</Label>
        <input
          className={cx(styles.slider, fieldClassName)}
          type="range"
          disabled={isDisabled}
          {...{ id, max, min, name, ref, step, value, onBlur, onChange }}
        />
        <Label ariaLabel={maxLabel}>{maxLabel}</Label>
      </div>
    )
  },
)

type ControlledSliderProps = ControlledFieldProps &
  Pick<SliderProps, 'labels' | 'max' | 'min' | 'step'>

export const ControlledSlider = ({
  control,
  fieldClassName,
  id,
  labels,
  max,
  min,
  name,
  step,
  isDisabled,
}: ControlledSliderProps) => (
  <Controller
    render={({ field: { name, ref, value, onBlur, onChange } }) => (
      <Slider
        {...{
          id,
          fieldClassName,
          labels,
          max,
          min,
          name,
          step,
          ref,
          value,
          onBlur,
          onChange,
          isDisabled,
        }}
      />
    )}
    {...{ control, name }}
  />
)
